import { useState, useCallback } from 'react';
import { useLocalStorage } from './useLocalStorage';

/**
 * Zentrale State-Verwaltung für die App
 * Gruppiert verwandte States zusammen
 * Alle Einstellungen werden automatisch in LocalStorage gespeichert
 */
export const useAppState = () => {
  // Canvas & Export
  const [canvasSize, setCanvasSize] = useLocalStorage('app_canvasSize', { width: 640, height: 640 });
  const [maxFrames, setMaxFrames] = useLocalStorage('app_maxFrames', 100);
  const [backgroundColor, setBackgroundColor] = useLocalStorage('app_backgroundColor', '#1a1a1a');
  const [exportYOLO, setExportYOLO] = useLocalStorage('app_exportYOLO', true);
  const [showBoundingBoxes, setShowBoundingBoxes] = useLocalStorage('app_showBoundingBoxes', true);
  const [annotationType, setAnnotationType] = useLocalStorage('app_annotationType', 'boundingBox'); // 'boundingBox' oder 'segmentation'

  // Objekt-Typ & Upload
  const [objectType, setObjectType] = useLocalStorage('app_objectType', 'model');
  // modelUrl und textureUrl: Speichere nur relative Pfade, keine Blob-URLs
  const [modelUrl, setModelUrlState] = useLocalStorage('app_modelUrl', '/simpleCube.glb');
  const [textureUrl, setTextureUrlState] = useLocalStorage('app_textureUrl', '/logo512.png');
  
  // Wrapper für setModelUrl: Speichere nur relative Pfade
  const setModelUrl = useCallback((url) => {
    // Wenn es eine Blob-URL ist, speichere nicht (funktioniert nach Neuladen nicht)
    if (url && url.startsWith('blob:')) {
      setModelUrlState(url);
    } else {
      // Speichere relative Pfade
      setModelUrlState(url);
    }
  }, [setModelUrlState]);
  
  // Wrapper für setTextureUrl: Speichere nur relative Pfade
  const setTextureUrl = useCallback((url) => {
    // Wenn es eine Blob-URL ist, speichere nicht (funktioniert nach Neuladen nicht)
    if (url && url.startsWith('blob:')) {
      setTextureUrlState(url);
    } else {
      // Speichere relative Pfade
      setTextureUrlState(url);
    }
  }, [setTextureUrlState]);

  // Kamera
  const [camera, setCamera] = useLocalStorage('app_camera', {
    fov: 75,
    near: 0.1,
    far: 5000,
    position: { x: 0, y: 0, z: 5 },
    rotation: { x: 0, y: 0, z: 0 }
  });

  // Material
  const [material, setMaterial] = useLocalStorage('app_material', {
    color: '#ffffff',
    metalness: 0.3,
    roughness: 0.7,
    clearcoat: 0,
    clearcoatRoughness: 0,
    reflectivity: 0.5,
    transmission: 0,
    emissive: '#ffffff',
    emissiveIntensity: 0.2
  });

  // Material Texturen (speichere nur Pfade, keine Blob-URLs)
  const [materialTextures, setMaterialTextures] = useLocalStorage('app_materialTextures', {
    albedo: null,
    normal: null,
    roughness: null,
    metallic: null,
    ao: null,
    emissive: null
  });

  // Objekt-Spawning (Model)
  const [modelSpawn, setModelSpawn] = useLocalStorage('app_modelSpawn', {
    minObjects: 1,
    maxObjects: 5,
    minPosition: { x: -3, y: -3, z: -2 },
    maxPosition: { x: 3, y: 3, z: 2 },
    minRotation: 0,
    maxRotation: 0,
    minDistance: 1,
    scale: 1
  });

  // Objekt-Spawning (Plane/Texture)
  const [planeSpawn, setPlaneSpawn] = useLocalStorage('app_planeSpawn', {
    minObjects: 5,
    maxObjects: 10,
    minPosition: { x: -2, y: -2, z: 0 },
    maxPosition: { x: 2, y: 2, z: 0 },
    minRotation: { x: 0, y: 0, z: 0 },
    maxRotation: { x: Math.PI, y: Math.PI, z: Math.PI },
    minDistance: 0.5
  });

  // Licht
  const [lighting, setLighting] = useLocalStorage('app_lighting', {
    ambient: {
      color: '#ffffff',
      intensity: 1.0
    },
    directional: {
      color: { r: 255, g: 255, b: 255 },
      intensity: 2.0,
      position: { x: 5, y: 5, z: 5 }
    },
    environment: {
      preset: 'sunset',
      blur: 1.0, // Initial komplett blurry
      customHDRUrl: `${process.env.PUBLIC_URL}/hdri/university_workshop_1k.hdr`
    }
  });

  // Randomizer - separate für jeden Aspekt
  const [randomizers, setRandomizers] = useLocalStorage('app_randomizers', {
    spawn: true,
    lighting: true,
    objectPosition: true,
    objectRotation: true,
    light: true,
    backgroundSpawner: true,
    foregroundSpawner: true,
    environment: false // HDR-Hintergrund Randomisierung
  });

  // Randomizer-Einstellungen (Bereiche)
  const [randomizerSettings, setRandomizerSettings] = useLocalStorage('app_randomizerSettings', {
    light: {
      intensityRange: { min: 0, max: 2 },
      positionRange: { minX: -50, maxX: 50, minY: -50, maxY: 50, minZ: -50, maxZ: 50 },
      colorRange: { min: 0, max: 255 }
    },
    backgroundSpawner: {
      minObjects: 20,
      maxObjects: 50,
      minSize: 0.5,
      maxSize: 3.0,
      positionRange: { minX: -10, maxX: 10, minY: -10, maxY: 10, minZ: -8, maxZ: -2 }
    },
    foregroundSpawner: {
      minObjects: 20,
      maxObjects: 50,
      minSize: 0.5,
      maxSize: 3.0,
      positionRange: { minX: -10, maxX: 10, minY: -10, maxY: 10, minZ: 2, maxZ: 8 }
    },
    environment: {
      frameInterval: 30 // Alle X Frames wird HDR gewechselt
    }
  });

  return {
    // Canvas & Export
    canvasSize,
    setCanvasSize,
    maxFrames,
    setMaxFrames,
    backgroundColor,
    setBackgroundColor,
    exportYOLO,
    setExportYOLO,
    showBoundingBoxes,
    setShowBoundingBoxes,
    annotationType,
    setAnnotationType,

    // Objekt-Typ & Upload
    objectType,
    setObjectType,
    modelUrl,
    setModelUrl,
    textureUrl,
    setTextureUrl,

    // Kamera
    camera,
    setCamera,

    // Material
    material,
    setMaterial,
    materialTextures,
    setMaterialTextures,

    // Spawning
    modelSpawn,
    setModelSpawn,
    planeSpawn,
    setPlaneSpawn,

    // Licht
    lighting,
    setLighting,
    setEnvironmentCustomHDR: (url) => {
      setLighting(prev => ({
        ...prev,
        environment: { ...prev.environment, customHDRUrl: url }
      }));
    },

    // Randomizer
    randomizers,
    setRandomizers,
    randomizerSettings,
    setRandomizerSettings
  };
};

